import { useState, type FormEvent } from 'react';
import { Button, IconButton } from './primitives/Button.js';
import { useTurnstile } from '@dedupr/turnstile';
import './AuditRequestModal.css';

/** Which call-to-action opened the form. Mirrors worker/audit-request.ts. */
export type AuditInterest = 'audit' | 'sandbox';

export interface AuditRequestModalProps {
  open: boolean;
  /** Defaults to the free audit; `sandbox` asks for the local-first build. */
  interest?: AuditInterest;
  onClose: () => void;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

const RECIPIENT_FALLBACK_EMAIL = 'hello@dedupr.cloud';

/**
 * One form, two intents. The fields are identical, so a second modal would be a
 * second copy of the Turnstile handling and the mailto fallback — the copy is
 * what differs, and it has to differ: a form headed "Run a free data audit" is
 * the wrong thing to show someone who clicked "Get the local sandbox".
 */
const COPY: Record<AuditInterest, { title: string; subtitle: string; submit: string; success: string }> = {
  audit: {
    title: 'Run a free data audit',
    subtitle: 'Two systems · 50k records · one findings report',
    submit: 'Send request',
    success: "We'll be in touch to get your audit started.",
  },
  sandbox: {
    title: 'Get the local sandbox',
    subtitle: 'Runs in your browser · your records never leave your device',
    submit: 'Send it over',
    success: "We'll send you the install link and a short guide to connecting your first system.",
  },
};

export function AuditRequestModal({ open, interest = 'audit', onClose }: AuditRequestModalProps) {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [systems, setSystems] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  const turnstile = useTurnstile();

  if (!open) return null;

  function reset() {
    setEmail('');
    setCompany('');
    setSystems('');
    setStatus('idle');
    setError('');
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setStatus('submitting');
    setError('');
    try {
      const response = await fetch('/api/audit-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, company, systems, interest, turnstileToken: turnstile.token }),
      });
      const data = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
      // Turnstile tokens are single-use — reset regardless of outcome so a
      // retry (or the next form open) gets a fresh one rather than a
      // guaranteed-stale token that fails verification again.
      turnstile.reset();
      if (!response.ok || !data?.ok) {
        setError(data?.error || 'Could not send your request right now. Please try again shortly.');
        setStatus('error');
        return;
      }
      setStatus('success');
    } catch {
      turnstile.reset();
      setError('Could not reach the server. Check your connection and try again.');
      setStatus('error');
    }
  }

  return (
    <div className="land-audit-overlay" role="dialog" aria-modal="true" aria-label={COPY[interest].title}>
      <div className="land-audit">
        <div className="land-audit__header">
          <div>
            <div className="land-audit__title">{COPY[interest].title}</div>
            <div className="land-audit__subtitle">{COPY[interest].subtitle}</div>
          </div>
          <IconButton size={32} aria-label="Close" onClick={handleClose}>
            ✕
          </IconButton>
        </div>

        <div className="land-audit__content">
          {status === 'success' ? (
            <div className="land-audit__success">
              <div className="land-audit__success-glyph">✓</div>
              <div className="land-audit__success-title">Request sent</div>
              <p className="land-audit__success-body">
                <strong>{email}</strong> — {COPY[interest].success}
              </p>
              <Button variant="primary" size="lg" onClick={handleClose}>
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="land-audit__form">
              <label className="land-audit__field">
                <span>Work email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@company.com"
                  disabled={status === 'submitting'}
                />
              </label>
              <label className="land-audit__field">
                <span>Company (optional)</span>
                <input
                  type="text"
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                  placeholder="Northgate Ltd"
                  disabled={status === 'submitting'}
                />
              </label>
              <label className="land-audit__field">
                <span>Systems you use (optional)</span>
                <input
                  type="text"
                  value={systems}
                  onChange={(event) => setSystems(event.target.value)}
                  placeholder="Salesforce, HubSpot, a pile of CSVs…"
                  disabled={status === 'submitting'}
                />
              </label>

              {status === 'error' && (
                <div className="land-audit__error">
                  {error}{' '}
                  <a href={`mailto:${RECIPIENT_FALLBACK_EMAIL}`}>Email us directly instead ↗</a>
                </div>
              )}

              <div ref={turnstile.containerRef} className="land-audit__turnstile" />
              {turnstile.error && <div className="land-audit__error">{turnstile.error}</div>}

              <Button
                variant="primary"
                size="lg"
                type="submit"
                disabled={status === 'submitting' || (turnstile.enabled && !turnstile.token)}
              >
                {status === 'submitting'
                  ? 'Sending…'
                  : turnstile.enabled && !turnstile.token
                    ? 'Verifying…'
                    : COPY[interest].submit}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
